import { useEffect, useState } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { docRegisterI18n } from '../../i18n';
import {
  getDocumentSendDetails,
  putDocumentSendUpdateStatus,
} from '../../../../services/documentManagementServices';
import { formatDate } from '../../../../utils/formatDate';
import { getDocumentSendSignedUrl } from '../../../../services/documentRegisterServices';
import usePermissionsSmartDocuments from 'permissions/modules/smartDocuments/usePermissionsSmartDocuments';

const ModalRegisterDetails = ({
  DesignSystemComponents,
  show,
  onClose,
  documentId,
  onUpdated,
  userId,
}) => {
  const { Modal, Badge, Alert, SvgIcon, Button, Skeleton } =
    DesignSystemComponents;

  const { t } = useScopedTranslation(docRegisterI18n);

  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);

  const { canDownloadDocument, canMarkAsLoaded } =
    usePermissionsSmartDocuments();

  const getStatusBadgeColor = (statusName) => {
    if (statusName === 'Cargado') return 'success';
    if (statusName === 'Pendiente en PJUD') return 'warning';
    if (statusName === 'Rechazado') return 'error';
    return 'light';
  };

  const getStatusDisplayName = (statusName) => {
    if (statusName === 'Pendiente en PJUD') return t('dr_status_pending');
    if (statusName === 'Cargado') return t('dr_status_loaded');
    if (statusName === 'Rechazado') return t('dr_status_rejected');
    return statusName;
  };

  useEffect(() => {
    if (!show || !documentId) return;

    const fetchDetails = async () => {
      try {
        setLoading(true);
        const result = await getDocumentSendDetails.getById(documentId);
        setDetails(result);
      } catch (error) {
        console.warn('Could not load register detail:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [show, documentId]);

  const handleMarkLoaded = async () => {
    try {
      await putDocumentSendUpdateStatus.putUpdateStatus({
        id: documentId,
        userId: userId,
        statusId: 6,
        observation: '',
        updatedAt: new Date()
          .toLocaleString('sv-SE', { timeZone: 'America/Santiago' })
          .replace(' ', 'T'),
      });

      if (onUpdated) onUpdated();

      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDownload = async () => {
    try {
      const { url } = await getDocumentSendSignedUrl.getSignedUrl(
        details.id,
        true,
        30
      );

      window.open(url, '_blank');
    } catch (err) {
      console.error('Error downloading document:', err);
      alert(t('dr_modal_downloadError'));
    }
  };

  return (
    <Modal
      title={t('dr_modal_title')}
      show={show}
      onClose={onClose}
      primaryBtnTxt={t('dr_modal_close')}
      primaryBtnAction={onClose}
      ctaAligment='right'
      width='600px'
    >
      <div>
        {loading && (
          <div className='row'>
            <div className='col-12 col-md-6'>
              <Skeleton height='500px' />
            </div>
            <div className='col-12 col-md-6'>
              <Skeleton height='500px' />
            </div>
          </div>
        )}
        {!loading && details && (
          <>
            <div className='row'>
              <div className='col-12'>
                {details.statusName === 'Pendiente en PJUD' &&
                  details.deliveryTypeIdDocument === 1 && (
                    <>
                      <Alert variant='warning' className='alert-on-modal mt-0'>
                        <div className='d-flex'>
                          <SvgIcon name='infoCircle' size='1.25rem' />
                          <div className='ms-2 d-flex flex-column align-items-end'>
                            <p className='caption mb-3'>
                              <span className='fw-bold'>
                                {t('dr_modal_alertTitle')}
                              </span>{' '}
                              {t('dr_modal_alertText')}
                            </p>
                            <Button
                              label={t('dr_modal_markLoaded')}
                              onClick={handleMarkLoaded}
                              size='sm'
                              variant='naked'
                              disabled={!canMarkAsLoaded}
                              className='fw-semibold'
                            />
                          </div>
                        </div>
                      </Alert>
                    </>
                  )}
              </div>
            </div>
            <div className='row'>
              <div className='col-12'>
                <div className='row mb-3'>
                  <div className='col-6'>
                    <p className='mini mb-1 font-light'>
                      {t('dr_modal_nameLabel')}
                    </p>
                    <p className='caption'>{details.name}</p>
                  </div>
                  <div className='col-6'>
                    <p className='mini mb-1 font-light'>
                      {t('dr_modal_statusLabel')}
                    </p>
                    <Badge
                      text={getStatusDisplayName(details.statusName)}
                      color={getStatusBadgeColor(details.statusName)}
                      size='sm'
                    />
                  </div>
                </div>
                <div className='row mb-3'>
                  <div className='col-6'>
                    <p className='mini mb-1 font-light'>
                      {t('dr_modal_docTypeLabel')}
                    </p>
                    <p className='caption'>{details.documentType}</p>
                  </div>
                  <div className='col-6'>
                    <p className='mini mb-1 font-light'>
                      {t('dr_modal_serviceLabel')}
                    </p>
                    <p className='caption'>{details.serviceName}</p>
                  </div>
                </div>
                <div className='row mb-3'>
                  <div className='col-6'>
                    <p className='mini mb-1 font-light'>
                      {t('dr_modal_caseLabel')}
                    </p>
                    <p className='caption'>{details.caseTitle}</p>
                    <p className='mini mb-1 font-light'>
                      {t('dr_modal_roleLabel')}: {details.caseCode}
                    </p>
                  </div>
                  <div className='col-6'>
                    <p className='mini mb-1 font-light'>
                      {t('dr_modal_deliveryTypeLabel')}
                    </p>
                    <Badge
                      text={
                        details.deliveryTypeIdDocument === 1
                          ? t('dr_modal_manual')
                          : t('dr_modal_automatic')
                      }
                      color={
                        details.deliveryTypeIdDocument === 1
                          ? 'info'
                          : 'light'
                      }
                      size='sm'
                    />
                  </div>
                </div>
                <div className='row mb-3'>
                  <div className='col-6'>
                    <p className='mini mb-1 font-light'>
                      {t('dr_modal_creatorLabel')}
                    </p>
                    <p className='caption'>{details.userName}</p>
                  </div>
                  <div className='col-6'>
                    <p className='mini mb-1 font-light'>
                      {t('dr_modal_createdAtLabel')}
                    </p>
                    <p className='caption'>
                      {details.createdAt ? formatDate(details.createdAt) : '-'}
                    </p>
                  </div>
                </div>
                <div className='row mb-3'>
                  <div className='col-6'>
                    <p className='mini mb-1 font-light'>
                      {t('dr_modal_reviewerLabel')}
                    </p>
                    <p className='caption'>
                      {details.statusChangedByName !== null
                        ? details.statusChangedByName
                        : '-'}
                    </p>
                  </div>
                  <div className='col-6'>
                    <p className='mini mb-1 font-light'>
                      {t('dr_modal_reviewedAtLabel')}
                    </p>
                    <p className='caption'>
                      {details.statusChangedAt
                        ? formatDate(details.statusChangedAt)
                        : '-'}
                    </p>
                  </div>
                </div>
                <div className='row mb-3'>
                  <div className='col-6'>
                    <p className='mini mb-1 font-light'>
                      {t('dr_modal_observationsLabel')}
                    </p>
                    <p className='caption'>
                      {details.observations ? details.observations : '-'}
                    </p>
                  </div>
                  <div className='col-6'>
                    <p className='mini mb-1 font-light'>
                      {t('dr_modal_documentSection')}
                    </p>
                    <Button
                      iconStart='paperclip'
                      label={details.name}
                      variant='naked'
                      size='sm'
                      onClick={handleDownload}
                      disabled={!canDownloadDocument}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ModalRegisterDetails;
